/* eslint-disable no-mixed-spaces-and-tabs */
import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import { gridClasses } from "@mui/x-data-grid-pro-6";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import ColumnCell from "../../../../../ts/components/cross-skilling/ui/cross-skill-table/ui/column-cell";
import ColumnEmployeeCell from "../../../../../ts/components/cross-skilling/ui/cross-skill-table/ui/column-employee-cell";
import ColumnGroupJobHeader from "../../../../../ts/components/cross-skilling/ui/cross-skill-table/ui/column-group-job-header";
import ColumnHeader from "../../../../../ts/components/cross-skilling/ui/cross-skill-table/ui/column-header";
import ColumnNoSkillHeader from "../../../../../ts/components/cross-skilling/ui/cross-skill-table/ui/column-no-skill-header";
import SortedDataGridPro6 from "../../../../../ts/design/sorted-datagrid-pro-6";
import { formatSkillScore } from "../../../../../ts/utils/helpers";
import useCrossSkillTableController from "../../controllers/use-cross-skill-table-controller";
import {
	createSkillColumn,
	createSkillColumnWithoutJobs,
	findOtherSkills,
	skillsWithoutJobs,
} from "./utils";

const COLUMN_CONFIGS = {
	companyEmployeeId: {
		field: "companyEmployeeId",
		headerNameKey: "cross-skilling_worker-id",
		valueGetter: (params) => params.row.companyEmployeeId,
		sortComparator: (v1, v2) => {
			if (v1 === v2) return 0;
			if (!v1) return 1;
			if (!v2) return -1;
			const num1 = /^\d+$/.test(v1.trim());
			const num2 = /^\d+$/.test(v2.trim());
			if (num1 && num2) return parseInt(v1, 10) - parseInt(v2, 10);
			if (num1) return -1;
			if (num2) return 1;
			return v1
				.trim()
				.localeCompare(v2.trim(), undefined, { sensitivity: "base" });
		},
	},
	jobTitle: {
		field: "jobTitle",
		headerNameKey: "cross-skilling_job-title",
		valueGetter: (params) =>
			params.row.jobTitle ? params.row.jobTitle.trim().toLowerCase() : "",
	},
	name: {
		field: "name",
		headerNameKey: "cross-skilling_worker-name",
		valueGetter: (params) => `${params.row.firstName} ${params.row.lastName}`,
		customCell: true,
	},
	workerType: {
		field: "workerType",
		headerNameKey: "cross-skilling_worker-type",
		valueGetter: (params) => params.row.workerType,
	},
	workerSubTypeText: {
		field: "workerSubTypeText",
		headerNameKey: "cross-skilling_worker-sub-type",
		width: 140,
		valueGetter: (params) => params.row.workerSubTypeText,
	},
	skillScore: {
		field: "skillScore",
		headerNameKey: "cross-skilling_total-skill-score",
		width: 160,
		valueFormatter: (params) => formatSkillScore(params.row?.skillScore) ?? "0",
	},
	primaryJob: {
		field: "primaryJob",
		headerNameKey: "cross-skilling_primary-job",
		valueGetter: (params) => params.row.primaryJob,
	},
	managerName: {
		field: "managerName",
		headerNameKey: "cross-skilling_reporting-manager",
		valueGetter: (params) => params.row.managerName?.toLowerCase(),
	},
	workCenter: {
		field: "workCenter",
		headerNameKey: "cross-skilling_work-center",
		valueGetter: (params) => params.row.workCenter,
	},
	location: {
		field: "location",
		headerNameKey: "cross-skilling_location",
		valueGetter: (params) => params.row.location,
	},
  skillsCoverage: {
    field: "skillsCoverage",
    headerName: "Skills Coverage",
    type: "number",
    valueGetter: (params) => {
      const rawValue = params.row?.skillsCoverage;
      if (!rawValue) return 0;
      return parseFloat(rawValue);
    },
  },
	shiftPatternName: {
		field: "shiftPatternName",
		headerNameKey: "cross-skilling_shift-pattern",
		valueGetter: (params) => params.row.shiftPatternName,
	},
	joiningDate: {
		field: "joiningDate",
		headerNameKey: "cross-skilling_joining-date",
		valueGetter: (params) => {
			return new Date(params?.value).getTime();
		},
	},
};

const createColumns = (selectedColumns, searchText, t) => {
	return selectedColumns
		.filter((column) => column.field in COLUMN_CONFIGS)
		.map((column) => {
			const config = COLUMN_CONFIGS[column.field];
			const headerName = t(config.headerNameKey);
			return {
				...config,
				headerName,
				width: config.width || 200,
				headerAlign: "left",
				renderHeader: () => <ColumnHeader text={headerName} />,
				renderCell: (params) =>
					config.customCell ? (
						<ColumnEmployeeCell params={params} searchText={searchText} />
					) : (
						<ColumnCell
							text={
								config.valueFormatter
									? config.valueFormatter(params)
									: params.row[config.field] ?? "-"
							}
							searchText={searchText}
						/>
					),
			};
		});
};

export default function CrossSkillingTable({
	data,
	filters,
	allSkillLevels,
	notAssignedSkillLevel,
	allJobs,
	filterStatus,
	allOrganizations,
	dataProcessed,
	selectedColumns,
	searchText,
	getFilter,
	onExport,
	onRowCountChange,
	appliedFilters,
}) {
	const { t } = useTranslation();
	const {
		apiRef,
		addSkillInterestStatus,
		rejectSkillInterestStatus,
		isJobFilter,
		isSkillPriorityFilter,
		skillPriorityIds,
		isColumnGroup,
		skillLevelIds,
		jobIdsSkillsArr,
		jobIds,
		filteredWorkersByLevel,
		filteredSkills,
		workers,
		handleAddSkillInterested,
		handleSkillLevels,
		columnGroup,
		updateWorkerSkillLevelStatus,
		height,
		allSkillsOfAllLocations,
		fetchManageWorkersDataStatus,
		skillMatrixFiltersStatus,
		skillMatrixDefaultFiltersStatus,
		skillsUpdatedListInSession,
		onUpdateSkillLevel,
		isWorkCenterFilter,
		isJobSkillLevelMapping,
    boxRef,
		selectedLocationIds,
	} = useCrossSkillTableController({
		data,
		filters,
		allSkillLevels,
		allJobs,
		getFilter,
		searchText,
		appliedFilters,
	});

	const isSkillInsightsEnabled = useSelector(
		(state) => state.user.userData.skillInsightsEnabled
	);

	const isSkillPriorityEnabled = useSelector(
		(state) => state.user.userData.skillPriorityEnabled
	);
	const isDrawerOpen = useSelector(
		(state) => state.navigationDrawer.isDrawerOpen
	);

	const [loading, setLoading] = useState(true);

	let rows = [];
	let col = [];

	col = createColumns(selectedColumns, searchText, t);

	const skillsCol = createSkillColumn(
		jobIdsSkillsArr,
		handleSkillLevels,
		allSkillLevels,
		notAssignedSkillLevel,
		handleAddSkillInterested,
		isJobFilter,
		searchText,
		isColumnGroup,
		false,
		skillsUpdatedListInSession,
		onUpdateSkillLevel
	);

	col = [...col, ...skillsCol];
	if (!isColumnGroup) {
		// Remove duplicate skills when not with column grouping
		col = col.filter(
			(v, i, a) => a.findIndex((v2) => v2.field === v.field) === i
		);
	}
	rows = skillLevelIds.length ? filteredWorkersByLevel ?? [] : workers ?? [];

	const uniqueSkills = jobIdsSkillsArr.filter(
		(obj, index, self) =>
			index === self.findIndex((o) => o.skillId === obj.skillId)
	);

	//check other skills
	if (
		filteredSkills.length > uniqueSkills.length &&
		isColumnGroup &&
		!isWorkCenterFilter &&
		!selectedLocationIds?.length
	) {
		const distinctSkills = findOtherSkills(filteredSkills, jobIdsSkillsArr);
		const isOther = true;

		const skillsCol1 = createSkillColumn(
			distinctSkills,
			handleSkillLevels,
			allSkillLevels,
			notAssignedSkillLevel,
			handleAddSkillInterested,
			isJobFilter,
			searchText,
			isColumnGroup,
			isOther,
			skillsUpdatedListInSession,
			onUpdateSkillLevel
		);

		let obj = {};
		let temp = [];
		distinctSkills.forEach((sk) => {
			temp.push({ field: sk.skillId + sk.jobColor });
			obj.groupId = "000";
			obj.headerName = t("cross-skilling_other-skills");
			obj.children = temp;
			obj.renderHeaderGroup = () => (
				<ColumnGroupJobHeader skill={sk} searchText={searchText} />
			);
		});
		if (Object.keys(obj).length > 0) columnGroup.push(obj);

		col = [...col, ...skillsCol1];
	}

	//create column for skills with no jobs for other skill's column group
	const skillsWithNoJobs = skillsWithoutJobs(allSkillsOfAllLocations, allJobs);
	if (
		skillsWithNoJobs.length > 0 &&
		!isJobFilter &&
		isColumnGroup &&
		!isWorkCenterFilter &&
		!selectedLocationIds?.length
	) {
		const columnsForOtherSkills = createSkillColumnWithoutJobs(
			skillsWithNoJobs,
			handleSkillLevels,
			allSkillLevels,
			notAssignedSkillLevel,
			handleAddSkillInterested,
			isJobFilter,
			searchText,
			skillsUpdatedListInSession,
			isSkillPriorityFilter,
			skillPriorityIds,
			onUpdateSkillLevel
		);
		col = [...col, ...columnsForOtherSkills];
	}

	// column for skills with no jobs
	if (jobIds.length > 0 && columnGroup.length > 0) {
		const jobsWithNoSkills = columnGroup.filter((x) => x.noSkill);
		jobsWithNoSkills.forEach((job) => {
			const noSkill = {
				skillId: -2,
				skillName: t("cross-skilling_no-skill"),
			};

			const noSkillColumn = [
				{
					field: `${job.groupId}-${noSkill.skillId}`,
					headerName: noSkill.skillName,
					width: 100,
					headerAlign: "center",
					renderHeader: () => <ColumnNoSkillHeader />,
				},
			];
			col = [...col, ...noSkillColumn];
		});
	}

	useEffect(() => {
		setLoading(!dataProcessed);
	}, [dataProcessed]);

	// Store filtered data and visible skills in a ref that can be accessed by parent
	const exportDataRef = React.useRef({ rows: [], skills: [], columnGrouping: [] });

	// Get skills without jobs for export (same conditions as column creation)
	const skillsWithNoJobsForExport = (
		!isJobFilter &&
		isColumnGroup &&
		!isWorkCenterFilter &&
		!selectedLocationIds?.length
	) ? skillsWithoutJobs(allSkillsOfAllLocations, allJobs) : [];

	// Get "No Skill" placeholder entries for jobs without mapped skills
	const noSkillPlaceholders = (jobIds.length > 0 && columnGroup.length > 0)
		? columnGroup
			.filter((x) => x.noSkill)
			.map((job) => ({
				skillId: -2,
				skillName: t("cross-skilling_no-skill"),
				name: t("cross-skilling_no-skill"),
				jobId: parseInt(job.groupId),
				jobColor: job.jobColor || "ebebeb",
			}))
		: [];

	// Combine regular skills with skills without jobs and no-skill placeholders for export
	const allExportSkills = [...(jobIdsSkillsArr || []), ...skillsWithNoJobsForExport, ...noSkillPlaceholders];

	// Update the ref with current data (no dependency on rows to avoid re-renders)
	exportDataRef.current = {
		rows: rows || [],
		skills: allExportSkills,
		columnGrouping: isColumnGroup ? columnGroup : [],
	};

	// Expose export function to parent via callback
	useEffect(() => {
		if (onExport) {
			onExport(() => exportDataRef.current);
		}
	}, [onExport]);

	// Notify parent when row count changes
	useEffect(() => {
		if (onRowCountChange) {
			onRowCountChange(rows?.length || 0);
		}
	}, [rows?.length, onRowCountChange]);

	return (
		<Box
      ref={boxRef}
			sx={{
        // Phones: fit the card and let the grid scroll sideways inside itself.
        width: { xs: "100%", md: "calc(100vw - 112px)" },
        minWidth: { xs: 0, md: "1364px" },
        height: `${height}px`,
			}}
		>
			<SortedDataGridPro6
				apiRef={apiRef}
				defaultSortField='name'
				key={`cross-skilling-datagrid-${isJobFilter}-${rows.length}`}
				pagination
				columnVisibilityModel={{
					skillScore: isSkillPriorityEnabled,
				}}
				slots={{
					loadingOverlay: LinearProgress,
				}}
				loading={
					fetchManageWorkersDataStatus === "pending" ||
					skillMatrixFiltersStatus === "pending" ||
					skillMatrixDefaultFiltersStatus === "pending" ||
					updateWorkerSkillLevelStatus === "pending" ||
					addSkillInterestStatus === "pending" ||
					rejectSkillInterestStatus === "pending" ||
					loading
				}
				pageSizeOptions={[25, 50, 100]}
				experimentalFeatures={{
					columnGrouping: isColumnGroup,
					lazyLoading: true,
				}}
				rows={rows}
				columns={col}
				disableRowSelectionOnClick
				columnGroupingModel={columnGroup}
				getRowHeight={() => 50} // Do not set to "auto" memory leak
				columnHeaderHeight={60}
				pinnedColumns={{
					left: [
						"companyEmployeeId",
						"jobTitle",
						"name",
						"workerType",
						"workerSubTypeText",
						"primaryJob",
						"managerName",
						"location",
						"workCenter",
						"shiftPatternName",
						"joiningDate",
						"skillScore",
            "skillsCoverage"
					],
				}}
				sx={{
					[`& .${gridClasses.cell}`]: {
						py: 1,
					},
					"& .MuiDataGrid-columnHeader": {
						padding: 0,
					},
					"& .MuiDataGrid-columnHeader:focus": {
						outline: "none",
					},
					"& .MuiDataGrid-row:hover": {
						backgroundColor: "white",
					},
					"& .MuiDataGrid-cell:hover": {
						backgroundColor: "#F8F8F8",
					},
					"& .MuiDataGrid-cell": {
						padding: 0,
					},
					"& .MuiDataGrid-cell:focus": {
						outline: "none",
					},
					"& .hideRightSeparator > .MuiDataGrid-columnSeparator": {
						display: "none",
					},
					"& .MuiDataGrid-pinnedColumnHeaders": {
						height: "100%",
						display: "flex",
						alignItems: "flex-start",
						justifyContent: "flex-end",
						boxShadow:
							"0px 3px 1px -2px rgba(0,0,0,0.0),0px 2px 2px 0px rgba(0,0,0,0.0),0px 1px 5px 0px rgba(0,0,0,0.12)",
					},
					"& .MuiDataGrid-columnHeader--filledGroup .MuiDataGrid-columnHeaderTitleContainer":
						{
							display: "block",
						},
					backgroundColor: "#fff",
					border: 0,
					"& .MuiTablePagination-root": {
						marginRight: "60px",
					},
				}}
				columnBuffer={5}
				columnThreshold={5}
				disableColumnMenu
			/>
		</Box>
	);
}
